import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState, useCallback, useRef } from "react";
import MapComponent from "../maps/MapComponent";
import Grid from "../Grid";
import { getOtherRefDetails } from "../../api/keyvalue";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import { requestActions } from "../../store/request-slice";
import ToggleSwitch from "../ToggleSwitch";
import LoadingSpinner from "../LoadingSpinner";
import { MAP_TYPES } from "../../const";

const LocationMap = React.memo(({ onUpdate, onClose, context }) => {
  const dispatch = useDispatch();
  const locations = useSelector(
    (state) => state.request.requestLocationsRequests,
  );
  const locationCustomerId = useSelector(
    (state) => state.request.reqLCustomerId,
  );

  const [isChanged, setIsChanged] = useState(false);
  const [originalLocations, setOriginalLocations] = useState([]);
  const [startLocation, setStartLocation] = useState(null);
  const [viaLocations, setViaLocations] = useState([]);
  const [endLocation, setEndLocation] = useState(null);
  const [locationPositions, setLocationPositions] = useState([]);
  const [gridContent, setGridContent] = useState([]);
  const [showDirections, setShowDirections] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState("");
  const [estimatedDistance, setEstimatedDistance] = useState("");

  const dragStartRef = useRef(null); // Use ref instead of state
  const dragStopRef = useRef(null);

  const combinedLocations = useCallback(() => {
    const orderedViaLocations = viaLocations.sort(
      (a, b) => a.rlkSeq - b.rlkSeq,
    ); // Ensure via points are sorted
    const combined = [
      startLocation,
      ...orderedViaLocations,
      endLocation,
    ].filter(Boolean); // Filter out any null values
    return combined;
  }, [startLocation, viaLocations, endLocation]);

  const fetchLocationDetails = useCallback(
    async (locationsList) => {
      const locationPos = [];
      let startLoc = null;
      let viaLocs = [];
      let endLoc = null;

      for (let location of locationsList) {
        if (location.rlkLocation) {
          const locationResponse = await getOtherRefDetails({
            type: "location_customer",
            value: locationCustomerId?.toString(),
            param2: location.rlkLocation?.toString(),
          });
          const locationDetails =
            locationResponse && locationResponse.length > 0
              ? locationResponse[0]
              : null;

          if (
            locationDetails &&
            locationDetails.retField10 &&
            locationDetails.retField9
          ) {
            const lat = parseFloat(locationDetails.retField10);
            const lng = parseFloat(locationDetails.retField9);

            if (!isNaN(lat) && !isNaN(lng)) {
              locationPos.push({ lat, lng });

              const formattedLocation = {
                ...location,
                locationName: locationDetails.retField1 || "",
                rlkAddress: locationDetails.retField2 || "",
                rlkEmail: locationDetails.retField6 || "",
                rlkContactNumber: locationDetails.retField5 || "",
                lat,
                lng,
                // Ensure formatted date fields are set
                rlkArrivalEtaFormatted: location.rlkArrivalEta
                  ? new Date(location.rlkArrivalEta).toLocaleString()
                  : "",
                rlkDepartureEtaFormatted: location.rlkDepartureEta
                  ? new Date(location.rlkDepartureEta).toLocaleString()
                  : "",
              };

              switch (location.rlkLocationType) {
                case 1:
                  startLoc = formattedLocation;
                  break;
                case 2:
                  endLoc = formattedLocation;
                  break;
                default:
                  viaLocs.push(formattedLocation);
                  break;
              }
            }
          }
        }
      }

      setStartLocation(startLoc);
      setViaLocations(viaLocs);
      setEndLocation(endLoc);
      setLocationPositions(locationPos);
      setGridContent([startLoc, ...viaLocs, endLoc].filter(Boolean));
    },
    [locationCustomerId],
  );

  useEffect(() => {
    if (locations.length > 0) {
      fetchLocationDetails(locations);
    }
  }, [locations, locationCustomerId, fetchLocationDetails]);

  useEffect(() => {
    const combined = combinedLocations();

    const initializedLocations = combined.map((loc, index) => ({
      ...loc,
      rlkSeq: index + 1, // Assign sequence numbers
    }));

    // Only update grid content if it's actually different to prevent unnecessary re-renders
    setGridContent((prevGridContent) => {
      const isEqual =
        JSON.stringify(prevGridContent) ===
        JSON.stringify(initializedLocations);
      return isEqual ? prevGridContent : initializedLocations;
    });
  }, [combinedLocations]);

  useEffect(() => {
    setOriginalLocations(gridContent);
  }, []);

  const handleReset = () => {
    setGridContent(originalLocations);
    setIsChanged(false);
  };

  const handleUpdate = () => {
    // Assuming `onUpdate` is a prop function that handles updating the parent state
    onUpdate(gridContent);
    onClose(); // Close the modal
  };

  const handleRowDragEnter = useCallback((e) => {
    dragStartRef.current = e.node?.rowIndex;
  }, []);

  const handleRowDragEnd = useCallback(
    (e) => {
      const startIndex = dragStartRef.current;
      const endIndex = e.overIndex;

      // If indices are invalid or no change in position, do nothing
      if (startIndex === null || endIndex === null || startIndex === endIndex) {
        dragStartRef.current = null;
        dragStopRef.current = null;
        return;
      }

      // Copy the current grid content to avoid mutating state directly
      const updatedLocations = [...gridContent];

      // Remove the dragged item and insert it at the new position
      const [movedItem] = updatedLocations.splice(startIndex, 1); // Remove item from start position
      updatedLocations.splice(endIndex, 0, movedItem); // Insert item at the new position

      // Update the sequence (rlkSeq) values for all items
      updatedLocations.forEach((location, index) => {
        location.rlkSeq = index + 1; // Recalculate sequence based on new order
      });

      // Update the grid content with the reordered locations
      setGridContent(updatedLocations);

      // Dispatch the updated locations to the Redux store
      dispatch(requestActions.updateLocations(updatedLocations));

      // Reset drag refs
      dragStartRef.current = null;
      dragStopRef.current = null;
    },
    [gridContent, dispatch],
  );

  const areLocationsEqual = (locations1, locations2) => {
    return (
      locations1.length === locations2.length &&
      locations1.every((loc, index) => {
        return loc.rlkSeq === locations2[index].rlkSeq; // Compares based on sequence only, adjust as needed
      })
    );
  };

  return (
    <div
      style={{
        width: "60vw",
        display: "flex",
        flexDirection: "column",
        gap: "5px",
      }}
    >
      {/*<ToggleSwitch*/}
      {/*  label={<b>Show Directions and Estimations</b>}*/}
      {/*  checked={showDirections}*/}
      {/*  onChange={() => setShowDirections(!showDirections)}*/}
      {/*/>*/}
      <div
        style={{
          width: "60vw",
          height: "60vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {locationPositions.length > 0 ? (
          <MapComponent
            type={MAP_TYPES.STATIC}
            initialLocations={combinedLocations()}
            showDirections={showDirections}
            onDirectionsChanged={(directionsResult) => {
              if (directionsResult?.routes?.length > 0) {
                const legs = directionsResult.routes[0].legs;

                // Total time
                const totalDuration = legs.reduce(
                  (acc, leg) => acc + leg.duration.value,
                  0,
                );
                const durationHours = Math.floor(totalDuration / 3600);
                const durationMinutes = Math.ceil((totalDuration % 3600) / 60);
                const formattedTime = `${durationHours > 0 ? durationHours + " hr " : ""}${
                  durationMinutes > 0 ? durationMinutes + " min" : ""
                }`;

                // Total distance
                const totalDistance = legs.reduce(
                  (acc, leg) => acc + leg.distance.value,
                  0,
                );
                const distanceInKm = (totalDistance / 1000).toFixed(1); // 1 decimal point
                const formattedDistance = `${distanceInKm} km`;

                setEstimatedTime(formattedTime);
                setEstimatedDistance(formattedDistance);
              }
            }}
          />
        ) : (
          <div>No location data available for the map.</div>
        )}
      </div>
      {showDirections && (estimatedTime || estimatedDistance) && (
        <div style={{ margin: "10px 0", fontWeight: "bold" }}>
          {estimatedTime && (
            <div>Estimated Time of Arrival : {estimatedTime}</div>
          )}
          {estimatedDistance && <div>Planned Km : {estimatedDistance}</div>}
        </div>
      )}
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Grid
          isLoading={false}
          onRowDragEnter={handleRowDragEnter}
          onRowDragEnd={handleRowDragEnd}
          rowDragManaged={true}
          pagination={false}
          cellSizeStrategy="fitCellContents"
          fitContentCells={[
            "rlkContactPersonValue",
            "rlkAddress",
            "locationName",
            "rlkEmail",
          ]}
          heading="Location List"
          config={[
            {
              headerName: "Sequence",
              field: "rlkSeq",
              rowDrag: context !== 3,
              suppressMovable: false,
            },
            {
              headerName: "Location",
              field: "locationName",
              cellRenderer: ResizableScrollingTextRenderer,
            },
            {
              headerName: "Location Address",
              field: "rlkAddress",
              cellRenderer: ResizableScrollingTextRenderer,
            },
            { headerName: "Contact Number", field: "rlkContactNumber" },
            {
              headerName: "Email",
              field: "rlkEmail",
              cellRenderer: ResizableScrollingTextRenderer,
            },
            {
              headerName: "Arrival",
              field: "rlkArrivalEtaFormatted",
              cellRenderer: ResizableScrollingTextRenderer,
            },
            {
              headerName: "Departure",
              field: "rlkDepartureEtaFormatted",
              cellRenderer: ResizableScrollingTextRenderer,
            },
          ]}
          content={gridContent}
        />
      )}
    </div>
  );
});

export default LocationMap;
